import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { invitationAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function InvitationGate({ asPage = false }) {
  const { activateAccount, selectCode, logout } = useAuth();
  const navigate = useNavigate();

  // Previously used codes (loaded from API)
  const [myCodes, setMyCodes] = useState([]);
  const [activeCode, setActiveCode] = useState('');
  const [loadingCodes, setLoadingCodes] = useState(true);

  // New code input
  const [newCode, setNewCode] = useState('');
  const [loading, setLoading] = useState(false);

  // Dropdown selected value
  const [selectedExisting, setSelectedExisting] = useState('');

  useEffect(() => {
    const fetchMyCodes = async () => {
      try {
        const res = await invitationAPI.myCodes();
        setMyCodes(res.data.codes || []);
        setActiveCode(res.data.active_code || '');
        if (res.data.codes?.length > 0) {
          setSelectedExisting(res.data.active_code || res.data.codes[0].code);
        }
      } catch {
        // Ignore — user may just not have any codes yet
      } finally {
        setLoadingCodes(false);
      }
    };
    fetchMyCodes();
  }, []);

  // ── Activate a NEW code ──────────────────────────────────────────────
  const handleActivateNew = async (e) => {
    e.preventDefault();
    if (!newCode.trim()) { toast.error('Please enter an invitation code'); return; }
    setLoading(true);
    try {
      await activateAccount(newCode.trim());
      toast.success('Account activated! Welcome to Craft Algorithm.');
      if (asPage) navigate('/');
    } catch (error) {
      const msg = error.response?.data?.error || 'Invalid invitation code';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ── Select an EXISTING code ──────────────────────────────────────────
  const handleSelectExisting = async () => {
    if (!selectedExisting) return;
    setLoading(true);
    try {
      await selectCode(selectedExisting);
      toast.success('Group selected!');
      if (asPage) navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to select code');
    } finally {
      setLoading(false);
    }
  };

  const hasPreviousCodes = myCodes.length > 0;

  return (
    <div className={`${asPage ? '' : 'min-h-screen'} flex items-center justify-center ${asPage ? 'py-8' : 'bg-gradient-to-br from-blue-900 via-gray-900 to-purple-900'}`}>
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <span className="text-5xl">🎟️</span>
            <h1 className="text-2xl font-bold text-gray-800 mt-4">
              {hasPreviousCodes ? 'Select Your Group' : 'Invitation Required'}
            </h1>
            <p className="text-gray-500 mt-2 text-sm">
              {hasPreviousCodes
                ? 'Choose an existing group or join a new one.'
                : 'Enter your invitation code to access the platform.'}
            </p>
          </div>

          {loadingCodes ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* ── Existing Codes Dropdown ── */}
              {hasPreviousCodes && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Groups
                  </label>
                  <select
                    value={selectedExisting}
                    onChange={(e) => setSelectedExisting(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2
                               focus:ring-blue-500 focus:border-transparent bg-white text-sm"
                    disabled={loading}
                  >
                    {myCodes.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}{c.note ? ` — ${c.note}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleSelectExisting}
                    disabled={loading || !selectedExisting}
                    className="w-full mt-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400
                               text-white py-3 rounded-lg font-medium transition-colors"
                  >
                    {loading ? 'Selecting...' : 'Continue with this group'}
                  </button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 bg-white text-gray-400">or join a new group</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── New Code Input ── */}
              <form onSubmit={handleActivateNew} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {hasPreviousCodes ? 'New Invitation Code' : 'Invitation Code'}
                  </label>
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                    placeholder="e.g. ptnk_grade10_class_computing"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2
                               focus:ring-blue-500 focus:border-transparent text-center font-mono text-lg"
                    autoFocus={!hasPreviousCodes}
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !newCode.trim()}
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    hasPreviousCodes
                      ? 'bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Verifying...
                    </span>
                  ) : hasPreviousCodes ? 'Join New Group' : 'Activate Account'}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center space-y-2">
            {asPage && (
              <button
                onClick={() => navigate(-1)}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors block mx-auto"
              >
                &larr; Back to Dashboard
              </button>
            )}
            {!asPage && (
              <button
                onClick={logout}
                className="text-sm text-gray-500 hover:text-red-600 transition-colors"
              >
                Sign out and use a different account
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
